import React, { HTMLAttributes, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import classNames from 'classnames';

import CartProductList from '../CartProductList/CartProductList';
import CartReinsurance from '../CartReinsurance/CartReinsurance';
import CartThreshold from '../CartThreshold/CartThreshold';
import DirectOrder from '../DirectOrder/DirectOrder';
import CheckoutSummaryTotal from 'components/Checkout/CheckoutSummary/CheckoutSummaryTotal/CheckoutSummaryTotal';
import CheckoutTitlePage from 'components/Checkout/CheckoutTitlePage/CheckoutTitlePage';
import FreeShippingThreshold from 'components/Checkout/Cart/FreeShippingThreshold/FreeShippingThreshold';
import OneyBanner from 'components/Checkout/Cart/OneyBanner/OneyBanner';
import PrivilegeCode from 'components/PrivilegeCode/PrivilegeCode';
import ButtonWithArrow from 'components/UI/Buttons/ButtonWithArrow/ButtonWithArrow';
import useHistory from 'hooks/useHistory';
import { getRoutesUri, getUri } from 'services/generic';
import { pushGtmEvent } from 'services/gtm/gtmEvents';
import { Cart as CartType } from 'types/Cart';

import styles from './CartWithProducts.module.scss';

export default function CartWithProducts({
  cart,
}: {
  cart: CartType;
}): JSX.Element {
  const { t } = useTranslation('checkout');
  const navigate = useNavigate();
  const { previousPath } = useHistory();
  const isConfirmBasketDisable =
    !cart.products.length ||
    !cart.isValidPrivilegeCode ||
    !!cart.proposedOfferedGifts.length;

  useEffect(() => {
    const pageFooterEl = document.querySelector('.page-footer');

    pageFooterEl?.classList.add(styles.pageFooter);

    return () => {
      pageFooterEl?.classList.remove(styles.pageFooter);
    };
  }, []);

  function handleConfirm() {
    pushGtmEvent({
      event: '_click_other',
      category: 'clic autre - checkout',
      element: 'validation panier',
    });

    navigate(getRoutesUri<string>('checkoutShipping'));
  }

  return (
    <>
      <CheckoutTitlePage
        title={t('CART_MY_BASKET')}
        goTo={previousPath ?? getUri('/')}
        classes={{ container: styles.title }}
      >
        <span className={styles.nbItems}>
          {`(${t('LABEL_NUMBER_ITEMS', {
            count: cart.nbProducts,
          })})`}
        </span>
      </CheckoutTitlePage>
      <div className={styles.content}>
        <div className={styles.page}>
          {cart.freeShippingThresholdInformation && (
            <FreeShippingThreshold
              message={cart.freeShippingThresholdInformation.message}
              amount={cart.freeShippingThresholdInformation.amount}
              percentage={cart.freeShippingThresholdInformation.percentage}
            />
          )}
          {cart?.thresholdMessages && (
            <CartThreshold thresholdMessages={cart.thresholdMessages} />
          )}
          <CartProductList
            products={cart?.products}
            selectedSpecialOffers={cart?.selectedSpecialOffers}
            proposedSpecialOffers={cart?.proposedSpecialOffers}
            selectedOfferedGifts={cart?.selectedOfferedGifts}
            proposedOfferedGifts={cart?.proposedOfferedGifts}
          />
          <DirectOrder />
        </div>
        <StickySummary className={styles.summary}>
          <PrivilegeCode />
          <CheckoutSummaryTotal cart={cart} isCartPage />
          <div className={styles.confirmBasket}>
            <ButtonWithArrow
              className={styles.button}
              classes={{
                arrow: styles.arrow,
              }}
              model={'primary'}
              onClick={handleConfirm}
              disabled={isConfirmBasketDisable}
            >
              {t('CART_TO_ORDER')}
            </ButtonWithArrow>
          </div>
          <OneyBanner cart={cart} />
          <CartReinsurance />
        </StickySummary>
      </div>
    </>
  );
}

function StickySummary({
  children,
  className,
  style,
  ...props
}: React.PropsWithChildren<HTMLAttributes<HTMLDivElement>>) {
  const [bannerHeight, setBannerHeight] = useState<number>(0);

  useEffect(() => {
    const targetNode = document.body;
    const config = { childList: true, subtree: true };

    function callback(
      mutationsList: MutationRecord[],
      observer: MutationObserver
    ) {
      for (const mutation of mutationsList) {
        if (mutation.type === 'childList') {
          const element = document.getElementById('headerBanner');
          if (element) {
            setBannerHeight(element.offsetHeight);
            observer.disconnect();
            break;
          }
        }
      }
    }

    const observer = new MutationObserver(callback);
    observer.observe(targetNode, config);

    return () => observer.disconnect();
  }, []);

  return (
    <div
      className={classNames(className, styles.summarySticky)}
      style={
        {
          ...style,
          '--bannerHeaderHeight': `${bannerHeight}px`,
        } as React.CSSProperties
      }
      {...props}
    >
      {children}
    </div>
  );
}
